/**
 * Browser session coordinator for the builder AI assistant.
 *
 * Owns request, conflict, model, history, and Undo state independently of the
 * Marionette drawer view so an exchange can finish while the drawer is closed.
 *
 */
/* global _, define, jQuery, nfAiChat, nfRadio */
define( 'controllers/ai/aiChatSession',[
	'controllers/ai/aiDraftReconciler',
	'controllers/ai/aiExchangeLifecycle',
], function ( draftReconciler, exchangeLifecycle ) {
	'use strict';

	let activeView = null;

	/*
	 * Text typed but not yet sent. The composer belongs to the drawer view and
	 * dies with it, so a user who steps out of chat to check a field name loses
	 * a message the box invited them to make 2,000 characters long. Holding it
	 * here, beside the request and history state the session already outlives
	 * the drawer with, restores it on reopen without a dialog. Deliberately not
	 * persisted: a reload discards unpublished form edits, so a message about
	 * changes that no longer exist would be worse than an empty box.
	 */
	let composerDraft = '';

	/*
	 * The nf_ai_chat envelope — action, nonce, and the form-id rule that keeps
	 * an unsaved form's localized "0" from reading as a real id — belongs to
	 * the lifecycle module, which this session already depends on. Sending from
	 * there keeps one statement of it for both the chat requests below and the
	 * accept/discard/revert/status transitions.
	 */
	const post = exchangeLifecycle.post;

	let applyingSnapshot = false;
	let busy = false;
	let formChangedMidRequest = false;
	let recoveryBlocked = false;
	let requestInFlight = false;
	let undoState = null;
	let undoWatchersBound = false;

	/**
	 * Read localized browser copy.
	 *
	 * @param {string} key Copy key.
	 * @return {string} Localized copy.
	 */
	const i18n = function ( key ) {
		if (
			'undefined' !== typeof nfAiChat &&
			nfAiChat.i18n &&
			nfAiChat.i18n[ key ]
		) {
			return nfAiChat.i18n[ key ];
		}
		return '';
	};

	/**
	 * Return the shared accepted/optimistic transcript.
	 *
	 * @return {Array} Conversation turns.
	 */
	const history = function () {
		if ( 'undefined' === typeof nfAiChat ) {
			return [];
		}
		if ( ! _.isArray( nfAiChat.history ) ) {
			nfAiChat.history = [];
		}
		return nfAiChat.history;
	};

	/**
	 * Read the selected provider.
	 *
	 * @return {string} Provider ID.
	 */
	const selectedProvider = function () {
		return ( 'undefined' !== typeof nfAiChat && nfAiChat.provider ) || '';
	};

	/**
	 * Read the selected model.
	 *
	 * @return {string} Model ID.
	 */
	const selectedModel = function () {
		return ( 'undefined' !== typeof nfAiChat && nfAiChat.model ) || '';
	};

	/**
	 * Remove the current Undo offer.
	 *
	 * @return {void}
	 */
	const retireUndo = function () {
		undoState = null;
		if ( activeView ) {
			activeView.removeUndoChip();
		}
	};

	/**
	 * Record a manual builder mutation and prevent stale result application.
	 *
	 * @return {void}
	 */
	const onFormChanged = function () {
		if ( applyingSnapshot ) {
			return;
		}
		retireUndo();
		if ( requestInFlight ) {
			formChangedMidRequest = true;
		}
	};

	/**
	 * Bind once to every form domain whose changes invalidate Undo.
	 *
	 * @return {void}
	 */
	const bindUndoWatchers = function () {
		if ( undoWatchersBound ) {
			return;
		}
		undoWatchersBound = true;

		const fields = nfRadio.channel( 'fields' ).request( 'get:collection' );
		if ( fields ) {
			fields.on( 'add remove change', onFormChanged );
		}
		const actions = nfRadio
			.channel( 'actions' )
			.request( 'get:collection' );
		if ( actions ) {
			actions.on( 'add remove change', onFormChanged );
		}
		const settings = nfRadio
			.channel( 'settings' )
			.request( 'get:settings' );
		if ( settings ) {
			settings.on( 'change', onFormChanged );
		}
	};

	/**
	 * Apply or restore a complete form snapshot.
	 *
	 * @param {Object}  form      Complete form draft.
	 * @param {boolean} restoring Whether to use trusted restore semantics.
	 * @return {Object} Reconciliation result.
	 */
	const reconcile = function ( form, restoring ) {
		let result;
		applyingSnapshot = true;
		try {
			result = restoring
				? draftReconciler.restore( form )
				: draftReconciler.apply( form );
		} finally {
			applyingSnapshot = false;
		}
		if ( ! result.success ) {
			return result;
		}
		nfRadio.channel( 'app' ).request( 'update:setting', 'clean', false );
		return result;
	};

	/**
	 * Finish a request and synchronize the active presentation.
	 *
	 * @return {void}
	 */
	const finishRequest = function () {
		busy = false;
		requestInFlight = false;
		if ( activeView ) {
			activeView.setBusy( false );
		}
	};

	/**
	 * Enter a blocking state when the exact browser/server outcome is unknown.
	 *
	 * The only safe recovery is to reload the persisted form. Ordinary chat
	 * operations and Publish remain unavailable until then.
	 *
	 * @param {Object|null} context Pending request context, when available.
	 * @return {void}
	 */
	const enterRecovery = function ( context ) {
		finishRequest();
		recoveryBlocked = true;
		busy = true;
		if ( 'undefined' !== typeof nfAiChat ) {
			nfAiChat.recoveryBlocked = true;
		}
		const builderEl = nfRadio.channel( 'app' ).request( 'get:builderEl' );
		if ( builderEl ) {
			jQuery( builderEl ).addClass( 'disable-main' );
		}
		/*
		 * Recovery is the last thing standing between a failed exchange and a
		 * silent drawer. Blocking the builder is best effort: if any step here
		 * throws, the administrator must still be told what happened rather
		 * than face a composer that accepts messages and answers nothing.
		 */
		try {
			nfRadio
				.channel( 'app' )
				.request( 'prevent:changeDomain', 'ai-recovery' );
			nfRadio
				.channel( 'drawer' )
				.request( 'prevent:close', 'ai-recovery' );
			retireUndo();
		} catch ( error ) {
			if ( window.console && window.console.error ) {
				window.console.error(
					'Ninja Forms AI: the builder could not be fully blocked for recovery.',
					error && error.message ? error.message : error
				);
			}
		}
		if ( context ) {
			removePendingTurn( context.userTurn );
		}
		if ( activeView ) {
			activeView.addMessage( 'error', i18n( 'recoveryRequired' ) );
			activeView.setBusy( true );
		}
	};

	/**
	 * Remove one optimistic turn when its exchange is rejected.
	 *
	 * @param {Object} turn Pending user turn.
	 * @return {void}
	 */
	const removePendingTurn = function ( turn ) {
		if ( _.last( history() ) === turn ) {
			history().pop();
		}
	};

	/**
	 * Reject a request and return its text to the active composer.
	 *
	 * @param {Object} context Pending request context.
	 * @param {string} message Localized error.
	 * @return {void}
	 */
	const rejectRequest = function ( context, message ) {
		finishRequest();
		removePendingTurn( context.userTurn );
		if ( activeView ) {
			activeView.addMessage( 'error', message );
			activeView.restoreInput( context.payload );
		}
	};

	/**
	 * Discard a provisional exchange before rejecting it locally.
	 *
	 * @param {string} token   Exchange token.
	 * @param {Object} context Pending request context.
	 * @param {string} message Localized error.
	 * @return {void}
	 */
	const discardRequest = function ( token, context, message ) {
		exchangeLifecycle.discard( token ).always( function () {
			rejectRequest( context, message );
		} );
	};

	/**
	 * Complete an exchange after the server accepts it.
	 *
	 * @param {Object} data        Provisional response.
	 * @param {Object} applyResult Browser reconciliation result.
	 * @return {void}
	 */
	const completeAcceptedRequest = function ( data, applyResult ) {
		finishRequest();
		const assistantTurn = _.extend(
			{
				role: 'assistant',
				content: data.reply,
				time: Math.floor( Date.now() / 1000 ),
			},
			nfAiChat.assistant
		);
		history().push( assistantTurn );
		if ( activeView ) {
			activeView.addMessage( 'assistant', data.reply, assistantTurn );
		}
		if ( data.changed && ! formChangedMidRequest ) {
			undoState = {
				snapshot: applyResult.before,
				exchangeToken: data.exchange_token,
			};
			if ( activeView ) {
				activeView.addUndoChip();
			}
		}
	};

	/**
	 * Roll back an applied draft when exchange acceptance fails.
	 *
	 * @param {Object} data        Provisional response.
	 * @param {Object} context     Pending request context.
	 * @param {Object} applyResult Browser reconciliation result.
	 * @return {void}
	 */
	const acceptFailed = function ( data, context, applyResult ) {
		let rolledBack;

		if ( data.changed && formChangedMidRequest ) {
			enterRecovery( context );
			return;
		}
		if ( data.changed && ! formChangedMidRequest ) {
			rolledBack = reconcile( applyResult.before, true );
			if ( rolledBack.success ) {
				nfRadio
					.channel( 'app' )
					.request( 'update:setting', 'clean', applyResult.wasClean );
			} else {
				exchangeLifecycle.discard( data.exchange_token );
				enterRecovery( context );
				return;
			}
		}
		discardRequest(
			data.exchange_token,
			context,
			i18n( 'exchangeCommitError' )
		);
	};

	/**
	 * Resolve an ambiguous accept response from durable server state.
	 *
	 * @param {Object} data        Provisional response.
	 * @param {Object} context     Pending request context.
	 * @param {Object} applyResult Browser reconciliation result.
	 * @return {void}
	 */
	const resolveAccept = function ( data, context, applyResult ) {
		exchangeLifecycle
			.status( data.exchange_token )
			.done( function ( response ) {
				const state = response && response.data;
				if ( exchangeLifecycle.completed( response, 'accepted' ) ) {
					completeAcceptedRequest( data, applyResult );
					return;
				}
				if ( state && false === state.pending ) {
					acceptFailed( data, context, applyResult );
					return;
				}
				enterRecovery( context );
			} )
			.fail( function () {
				enterRecovery( context );
			} );
	};

	/**
	 * Accept an exchange after its draft has been verified.
	 *
	 * @param {Object} data        Provisional response.
	 * @param {Object} context     Pending request context.
	 * @param {Object} applyResult Browser reconciliation result.
	 * @return {void}
	 */
	const acceptRequest = function ( data, context, applyResult ) {
		exchangeLifecycle
			.accept( data.exchange_token )
			.done( function ( response ) {
				if ( ! exchangeLifecycle.completed( response, 'accepted' ) ) {
					resolveAccept( data, context, applyResult );
					return;
				}
				completeAcceptedRequest( data, applyResult );
			} )
			.fail( function () {
				resolveAccept( data, context, applyResult );
			} );
	};

	/**
	 * Route the initial provisional response.
	 *
	 * @param {Object} response WordPress AJAX response.
	 * @param {Object} context  Pending request context.
	 * @return {void}
	 */
	const handleResponse = function ( response, context ) {
		let applyResult = { success: true, before: null };
		const data = response && response.data;
		let message;

		if ( data && data.provider_required && ! data.reply ) {
			finishRequest();
			removePendingTurn( context.userTurn );
			nfAiChat.providers = _.isArray( data.providers )
				? data.providers
				: [];
			nfAiChat.provider = data.provider || '';
			nfAiChat.model = data.model || '';
			if ( activeView ) {
				activeView.renderProviderSelector();
				activeView.addMessage( 'error', data.message );
				activeView.restoreInput( context.payload );
			}
			return;
		}
		if ( ! data || ! data.reply || ! data.exchange_token ) {
			message =
				response && response.errors && response.errors.length
					? response.errors[ 0 ]
					: i18n( 'error' );
			rejectRequest( context, message );
			return;
		}
		if ( formChangedMidRequest ) {
			discardRequest(
				data.exchange_token,
				context,
				i18n( 'changedWhileWorking' )
			);
			return;
		}
		if ( data.changed ) {
			applyResult = reconcile( data.form, false );
		}
		if ( ! applyResult.success ) {
			if ( applyResult.error && applyResult.error.rollbackError ) {
				exchangeLifecycle.discard( data.exchange_token );
				enterRecovery( context );
				return;
			}
			discardRequest(
				data.exchange_token,
				context,
				i18n( 'applyError' )
			);
			return;
		}
		acceptRequest( data, context, applyResult );
	};

	/**
	 * Send the current visible draft with one user request.
	 *
	 * @param {string} userText User request.
	 * @return {void}
	 */
	const send = function ( userText ) {
		if ( busy || 'undefined' === typeof nfAiChat ) {
			return;
		}
		retireUndo();
		bindUndoWatchers();
		requestInFlight = true;
		formChangedMidRequest = false;
		const userTurn = _.extend(
			{
				role: 'user',
				content: userText,
				time: Math.floor( Date.now() / 1000 ),
			},
			nfAiChat.currentUser
		);
		const payload = { message: userText };
		const context = { payload, userTurn };
		history().push( userTurn );
		busy = true;
		if ( activeView ) {
			activeView.addMessage( 'user', userText, userTurn );
			activeView.setBusy( true );
		}

		post(
			_.extend(
				{
					provider: selectedProvider(),
					model: selectedModel(),
					draft: JSON.stringify( draftReconciler.serialize( true ) ),
				},
				payload
			)
		)
			.done( function ( response ) {
				handleResponse( response, context );
			} )
			.fail( function () {
				rejectRequest( context, i18n( 'error' ) );
			} );
	};

	/**
	 * Restore the AI-applied state when Undo persistence fails.
	 *
	 * @param {Object} state        Undo state.
	 * @param {Object} appliedState AI-applied snapshot.
	 * @return {void}
	 */
	const undoPersistenceFailed = function ( state, appliedState ) {
		let restored = { success: false };
		if ( ! formChangedMidRequest ) {
			restored = reconcile( appliedState, true );
		}
		finishRequest();
		if ( restored.success ) {
			undoState = state;
		} else {
			enterRecovery( null );
			return;
		}
		if ( activeView ) {
			activeView.addMessage( 'error', i18n( 'undoPersistError' ) );
			if ( undoState ) {
				activeView.addUndoChip();
			}
		}
	};

	/**
	 * Complete a durable Undo transition in browser history.
	 *
	 * @return {void}
	 */
	const completeUndo = function () {
		finishRequest();
		retireUndo();
		const now = Math.floor( Date.now() / 1000 );
		const userTurn = _.extend(
			{ role: 'user', content: i18n( 'undoRequest' ), time: now },
			nfAiChat.currentUser
		);
		const assistantTurn = _.extend(
			{
				role: 'assistant',
				content: i18n( 'undoComplete' ),
				time: now,
			},
			nfAiChat.assistant
		);
		history().push( userTurn, assistantTurn );
		if ( activeView ) {
			activeView.addMessage( 'user', userTurn.content, userTurn );
			activeView.addMessage(
				'assistant',
				assistantTurn.content,
				assistantTurn
			);
		}
	};

	/**
	 * Resolve an ambiguous Undo response from durable server state.
	 *
	 * @param {Object} state        Undo state.
	 * @param {Object} appliedState AI-applied snapshot.
	 * @return {void}
	 */
	const resolveUndo = function ( state, appliedState ) {
		exchangeLifecycle
			.status( state.exchangeToken )
			.done( function ( response ) {
				const durableState = response && response.data;
				if ( exchangeLifecycle.completed( response, 'reverted' ) ) {
					completeUndo();
					return;
				}
				if ( durableState && false === durableState.accepted ) {
					undoPersistenceFailed( state, appliedState );
					return;
				}
				enterRecovery( null );
			} )
			.fail( function () {
				enterRecovery( null );
			} );
	};

	/**
	 * Persist a verified browser Undo.
	 *
	 * @param {Object} state        Undo state.
	 * @param {Object} appliedState AI-applied snapshot.
	 * @return {void}
	 */
	const persistUndo = function ( state, appliedState ) {
		exchangeLifecycle
			.revert( state.exchangeToken )
			.done( function ( response ) {
				if ( ! exchangeLifecycle.completed( response, 'reverted' ) ) {
					resolveUndo( state, appliedState );
					return;
				}
				completeUndo();
			} )
			.fail( function () {
				resolveUndo( state, appliedState );
			} );
	};

	/**
	 * Undo the latest accepted AI draft change.
	 *
	 * @return {void}
	 */
	const undo = function () {
		if ( busy || ! undoState || 'undefined' === typeof nfAiChat ) {
			return;
		}
		const state = undoState;
		bindUndoWatchers();
		requestInFlight = true;
		formChangedMidRequest = false;
		busy = true;
		if ( activeView ) {
			activeView.setBusy( true );
		}
		const result = reconcile( state.snapshot, true );
		if ( ! result.success ) {
			if ( result.error && result.error.rollbackError ) {
				enterRecovery( null );
				return;
			}
			finishRequest();
			if ( activeView ) {
				activeView.addMessage( 'error', i18n( 'undoApplyError' ) );
				activeView.addUndoChip();
			}
			return;
		}
		formChangedMidRequest = false;
		persistUndo( state, result.before );
	};

	/**
	 * Persist a model choice before showing it as selected.
	 *
	 * @param {Object} choice Exact provider/model choice.
	 * @return {void}
	 */
	const chooseModel = function ( choice ) {
		if ( busy || 'undefined' === typeof nfAiChat ) {
			return;
		}
		busy = true;
		if ( activeView ) {
			activeView.setBusy( true );
		}
		post( {
			select_model: 1,
			provider: choice.provider,
			model: choice.model,
		} )
			.done( function ( response ) {
				if (
					! response ||
					! response.data ||
					true !== response.data.selected
				) {
					if ( activeView ) {
						activeView.renderProviderSelector();
						activeView.addMessage(
							'error',
							i18n( 'selectionPersistError' )
						);
					}
					return;
				}
				nfAiChat.provider = choice.provider;
				nfAiChat.model = choice.model;
				nfAiChat.providerDisconnected = false;
				if ( activeView ) {
					activeView.modelPicker.commit( choice );
					activeView.focusInput();
				}
			} )
			.fail( function () {
				if ( activeView ) {
					activeView.renderProviderSelector();
					activeView.addMessage(
						'error',
						i18n( 'selectionPersistError' )
					);
				}
			} )
			.always( function () {
				busy = false;
				if ( activeView ) {
					activeView.setBusy( false );
				}
			} );
	};

	/**
	 * Clear accepted context and browser Undo while retaining form/model.
	 *
	 * @return {void}
	 */
	const clear = function () {
		if ( busy || 'undefined' === typeof nfAiChat ) {
			return;
		}
		const retainedProvider = nfAiChat.provider;
		const retainedModel = nfAiChat.model;
		busy = true;
		if ( activeView ) {
			activeView.setBusy( true );
		}
		post( { clear_history: 1 } )
			.done( function ( response ) {
				const data = response && response.data;
				if ( ! data || ! data.cleared ) {
					if ( activeView ) {
						activeView.addMessage(
							'error',
							i18n( 'startNewChatError' )
						);
					}
					return;
				}
				retireUndo();
				composerDraft = '';
				history().splice( 0, history().length );
				nfAiChat.provider = retainedProvider;
				nfAiChat.model = retainedModel;
				if ( activeView ) {
					activeView.resetConversation();
				}
			} )
			.fail( function () {
				if ( activeView ) {
					activeView.addMessage(
						'error',
						i18n( 'startNewChatError' )
					);
				}
			} )
			.always( function () {
				busy = false;
				if ( activeView ) {
					activeView.setBusy( false );
				}
			} );
	};

	return {
		/**
		 * Attach the currently rendered chat view.
		 *
		 * @param {Object} view Chat drawer view.
		 * @return {void}
		 */
		attach( view ) {
			activeView = view;
		},
		/**
		 * Detach a chat view when it closes.
		 *
		 * @param {Object} view Chat drawer view.
		 * @return {void}
		 */
		detach( view ) {
			if ( activeView === view ) {
				activeView = null;
			}
		},
		clear,
		chooseModel,
		/**
		 * Read text typed into the composer but not yet sent.
		 *
		 * @return {string} Retained composer text.
		 */
		composerDraft() {
			return composerDraft;
		},
		/**
		 * Retain composer text across a drawer close.
		 *
		 * @param {string} text Current composer text.
		 * @return {void}
		 */
		rememberComposer( text ) {
			composerDraft = 'string' === typeof text ? text : '';
		},
		/**
		 * Report whether an accepted AI change can be undone.
		 *
		 * @return {boolean} Whether Undo is available.
		 */
		hasUndo() {
			return null !== undoState;
		},
		history,
		i18n,
		/**
		 * Report whether the session is processing or requires recovery.
		 *
		 * @return {boolean} Whether ordinary chat operations are blocked.
		 */
		isBusy() {
			return busy || recoveryBlocked;
		},
		/**
		 * Read the localized provider catalog.
		 *
		 * @return {Array} Available provider rows.
		 */
		providers() {
			return 'undefined' !== typeof nfAiChat &&
				_.isArray( nfAiChat.providers )
				? nfAiChat.providers
				: [];
		},
		selectedModel,
		selectedProvider,
		send,
		undo,
	};
} );

